/**
 * Routes and the signed-in shell.
 *
 * Clerk's <Authenticated>/<Unauthenticated> equivalents come from convex/react
 * rather than @clerk/clerk-react: they track whether Convex itself has a valid
 * token, so children never render a query before the token is in place and
 * flash an auth error.
 */
import { Routes, Route, Navigate } from "react-router-dom";
import { Authenticated, AuthLoading, Unauthenticated } from "convex/react";

import { AppShell } from "@/components/app-shell";
import { SignInScreen } from "@/pages/sign-in";
import { BootSplash } from "@/components/boot-splash";
import { Toaster } from "@/components/ui/toaster";
import { StatusBanners } from "@/components/status-banners";
import { TooltipProvider } from "@/components/ui/tooltip";
import { WorkModeProvider } from "@/hooks/use-work-mode";
import { useEnsureUser } from "@/hooks/use-ensure-user";

import DashboardPage from "@/pages/dashboard";
import DashplanPage from "@/pages/dashplan";
import DailiesPage from "@/pages/dailies";
import BookmarksPage from "@/pages/bookmarks";
import FocusPage from "@/pages/focus";
import QuestsPage from "@/pages/quests";
import NotesPage from "@/pages/notes";
import ListsPage from "@/pages/lists";
import SettingsPage from "@/pages/settings";
import NotFoundPage from "@/pages/not-found";

/**
 * Mirrors the Clerk identity into Convex before rendering anything that reads
 * user data. Every query calls `requireUser`, which throws until the row
 * exists, so the app waits here rather than rendering a wall of errors.
 */
function SignedInApp() {
  const status = useEnsureUser();

  if (status === "pending") {
    return <BootSplash message="Setting up your workspace" />;
  }

  if (status === "error") {
    return (
      <BootSplash
        message="Could not reach Convex"
        detail="Check that VITE_CONVEX_URL points at a running deployment and that CLERK_JWT_ISSUER_DOMAIN is set on it."
        tone="error"
      />
    );
  }

  return (
    <WorkModeProvider>
      <Routes>
        <Route element={<AppShell />}>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/dashplan" element={<DashplanPage />} />
          <Route path="/dashplan/:date" element={<DashplanPage />} />
          <Route path="/dailies" element={<DailiesPage />} />
          <Route path="/bookmarks" element={<BookmarksPage />} />
          <Route path="/bookmarks/:slug" element={<BookmarksPage />} />
          <Route path="/focus" element={<FocusPage />} />
          <Route path="/quests" element={<QuestsPage />} />
          <Route path="/notes" element={<NotesPage />} />
          <Route path="/lists" element={<ListsPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          {/* The old Replit app used /categories for what is now /bookmarks. */}
          <Route path="/categories" element={<Navigate to="/bookmarks" replace />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </WorkModeProvider>
  );
}

export default function App() {
  return (
    <TooltipProvider delayDuration={300}>
      {/* Above every auth state — an offline or update notice is relevant
          whether or not the user is signed in. */}
      <StatusBanners />

      <AuthLoading>
        <BootSplash message="Signing you in" />
      </AuthLoading>

      <Unauthenticated>
        <SignInScreen />
      </Unauthenticated>

      <Authenticated>
        <SignedInApp />
      </Authenticated>

      <Toaster />
    </TooltipProvider>
  );
}
